import { useEffect, useRef, useState } from "react";
import Reveal from "@Components/Reveal/Reveal";
// The Lottie host the hero's headline mark uses — a <span>, with lottie-web dynamically imported so
// it stays out of the server bundle.
import InlineLottie from "@Components/InlineLottie/InlineLottie";
import styles from "./WhyPartners.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images and animations come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ListWithUsV2");

// ── THE MARKS ──────────────────────────────────────────────────────────────
// ⚠️  SEVEN OF THE TEN WERE REPLACED because they did not describe their figure. The set was
// inherited wholesale from the Career page's Values marks, so it was decorative from the start and
// drifted straight past the labels: a shield-and-key on "Beds listed", an AI chip on "Properties
// live", a magnifier on "Cities covered", a graduation cap on "$1Bn+ Bookings delivered" and a
// lightning bolt on "Avg. booking value". Now each mark names its figure: bed, home, map, flag
// (countries live) and people for supply; star (Trustpilot), globe (nationalities), calendar check
// (bookings), cap (students) and moon (nights per booking) for demand. The flag and globe are
// both teal, so swapping them kept the colour arrangement intact.
// The Career page's Values set, recoloured to FIVE colours sampled off a reference:
//
//   indigo #6466E9    purple #845EEE    blue #487EF7    teal #55B5A6    sky #4BA3E3
//
// ⚠️  THE COLOUR IS BAKED INTO EACH FILE, so it travels with the ICON and not with the position. The
// arrangement matters: over the 5x2 grid, row one runs the palette in order and row two is the same
// order rotated by two, so no two neighbours share a colour in either direction. REORDERING
// `FIGURES` BREAKS THAT and cannot be fixed in CSS — the files would have to be regenerated.
//
// Generated from the original $neutral7 exports rather than from an earlier all-blue set, so a colour
// is never a tint of a tint. That blue set is still on disk, unused.
//
// ⚠️  About 300KB OF JSON for ten decorative marks (the calendar alone is 113KB). Worth knowing
// before this grows: it used to sit behind a variant switch so it only loaded on demand, and that switch is gone
// — this is now on the page unconditionally.
// Ordered as the grid reads. The colour suffix is the CELL's colour, not the icon's
// — see the arrangement note above.
// The heading's animated mark — the supplied earth (512x512, 6 layers, 114KB, 120
// frames at 29.97fps, so a 4s loop against the previous globe's 3s).
//
// ⚠️  NOT `assets/lottie/globe.json`, which is a different, unused 512x512 / 264KB
// file sitting in this folder under a confusingly similar name. This is the artwork
// handed over for this heading; that one is dead weight nothing imports.
const lottieEarthMark = asset("why-partners-earth-mark.json");
const lottieBed = asset("why-partners-bed-indigo.json");
const lottieHome = asset("why-partners-home-purple.json");
const lottieMap = asset("why-partners-map-blue.json");
const lottieGlobe = asset("why-partners-globe-teal.json");
const lottieUsers = asset("why-partners-users-sky.json");
// The star, flag, calendar and moon are Iconly Motion marks, the same library as the rest of
// the set, recoloured with the same fill/stroke swap that produced `tinted/` (regenerating
// graduation-sky that way reproduces the committed file exactly). The untinted originals sit
// beside the others in `assets/lottie/`.
const lottieStar = asset("why-partners-star-blue.json");
const lottieFlag = asset("why-partners-flag-teal.json");
const lottieCalendar = asset("why-partners-calendar-check-sky.json");
const lottieCap = asset("why-partners-graduation-indigo.json");
const lottieMoon = asset("why-partners-moon-purple.json");

/**
 * "Why partners list with amber" — ten figures in one panel, each with a coloured mark.
 *
 * ── WHAT THIS USED TO BE ───────────────────────────────────────────────────
 * ⚠️  THERE WAS A VARIANT SWITCH HERE, and it is gone along with the treatment it toggled. The other
 * variant implemented Figma node 5985:12536 literally: a 5x2 grid of ruled `Article` cells, each with
 * a one-line title merging the figure with its label, a sentence at 60% black, and a 108px line
 * diagram pinned to the bottom. The switch existed so the two could be compared in the running page
 * rather than by rebuilding, and this is the one that was chosen.
 *
 * What came out with it: `Diagram.tsx`, `Diagram.module.scss` and the nineteen exported SVG fragments
 * in `assets/icons/connect` are now unreferenced, as are the ten drafted description sentences that
 * variant carried. Nothing was deleted — the files are on disk if that treatment is ever wanted back.
 *
 * Six treatments preceded the Figma one, and the shape of this section is the residue of all of them:
 * a bento of four figures with a dashboard panel each (did not scale to ten), a bare hairline grid
 * (read as a spreadsheet), a tinted panel with dashed dividers, a dark navy band with muted figures,
 * and the two Figma variants.
 *
 * ── The figures ────────────────────────────────────────────────────────────
 * Row one is SUPPLY, row two is DEMAND — the split the brief hands them over in. The brief also
 * gave each figure a sentence; they were tried under the labels and dropped as too busy for a
 * ten-cell grid, so only the short label shows.
 */

interface Figure {
  /** The digits — the card's title. */
  value: string;
  /** The figure's name — the card's description. */
  label: string;
  /** The Lottie mark. Its colour is baked in; see the note above. */
  lottie: string;
}

const FIGURES: Figure[] = [
  // Supply
  { value: "2M+", label: "list.v2.whypartners.bedslisted", lottie: lottieBed },
  { value: "5K+", label: "list.v2.whypartners.propertieslive", lottie: lottieHome },
  { value: "260+", label: "list.v2.whypartners.citiescovered", lottie: lottieMap },
  { value: "10+", label: "list.v2.whypartners.countrieslive", lottie: lottieFlag },
  { value: "500+", label: "list.v2.whypartners.supplypartners", lottie: lottieUsers },

  // Demand
  { value: "4.8", label: "list.v2.whypartners.trustpilot", lottie: lottieStar },
  { value: "80+", label: "list.v2.whypartners.nationalities", lottie: lottieGlobe },
  { value: "200K+", label: "list.v2.whypartners.bookings", lottie: lottieCalendar },
  { value: "600K+", label: "list.v2.whypartners.students", lottie: lottieCap },
  { value: "300+", label: "list.v2.whypartners.nights", lottie: lottieMoon },
];

/**
 * The mark's layout box.
 *
 * These Lottie comps are mostly empty canvas — their ink fills about 0.85 of the square — so the
 * VISIBLE mark is roughly 25px, which is what has to be judged. It was 22 when the cell was 80px
 * tall; opening the cell to 105px left it looking undersized, because a mark reads relative to the
 * room around it rather than in absolute px.
 */
const MARK_SIZE = 32;

/**
 * Each cell's own colour, for its hover shimmer — the same colour its mark is tinted, in the same
 * order as `FIGURES`, so the light that sweeps the text matches the icon beside it.
 */
const ACCENTS = [
  "#6466E9",
  "#845EEE",
  "#487EF7",
  "#55B5A6",
  "#4BA3E3",
  "#487EF7",
  "#55B5A6",
  "#4BA3E3",
  "#6466E9",
  "#845EEE",
];

// ── COUNT-UP ────────────────────────────────────────────────────────────────
// Each figure counts from 0 to its value once the panel is on screen, one cell after the other in
// reading order. Kept deliberately quiet: a short ease-out, no scale or colour change, and the
// cells overlap rather than waiting for each other, so the whole panel settles in about two seconds.

/** How long one figure takes to count. */
const COUNT_MS = 900;
/** The delay between one cell starting and the next. */
const COUNT_STAGGER_MS = 110;

/**
 * Splits a figure into what counts and what does not: "200K+" -> 200 with suffix "K+", "4.8" ->
 * 4.8 with one decimal. Returns null for anything without a number in it, which then renders as-is.
 */
const parseFigure = (value: string) => {
  const match = value.match(/^([^\d]*)(\d+(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  const [, prefix, digits, suffix] = match;
  return {
    prefix,
    suffix,
    target: parseFloat(digits),
    decimals: digits.includes(".") ? digits.split(".")[1].length : 0,
  };
};

/**
 * ⚠️  THE SERVER RENDERS THE FINAL VALUE, and the client drops it to zero on mount. So the figures
 * are correct in the HTML (search engines, no-JS, a slow hydrate), and the count only ever runs in
 * a browser that can show it. The reset happens on mount rather than when the panel scrolls in, so
 * a reader never sees the real number flash back to 0.
 *
 * Small targets count with one decimal ("2M+" runs 0.0 -> 2.0) and snap to the exact string at the
 * end — whole steps would give "2M+" three frames and read as a jump rather than a count.
 */
const CountUp = ({ value, index, run }: { value: string; index: number; run: boolean }) => {
  const figure = parseFigure(value);
  const [shown, setShown] = useState(value);
  const reduced = useRef(false);

  const format = (n: number) => {
    if (!figure) return value;
    const decimals = figure.target < 10 ? Math.max(1, figure.decimals) : figure.decimals;
    return `${figure.prefix}${n.toFixed(decimals)}${figure.suffix}`;
  };

  useEffect(() => {
    reduced.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (figure && !reduced.current) setShown(format(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!figure || !run || reduced.current) return undefined;
    let frame = 0;
    const start = performance.now() + index * COUNT_STAGGER_MS;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - start) / COUNT_MS));
      const eased = 1 - (1 - t) ** 3;
      setShown(t < 1 ? format(figure.target * eased) : value);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  return (
    <>
      {/* The moving digits are hidden from assistive tech, which reads the real figure instead —
        otherwise a screen reader can catch it mid-count and announce "1.3M+". */}
      <span aria-hidden="true">{shown}</span>
      <span className={styles.srOnly}>{value}</span>
    </>
  );
};

/**
 * The heading is the ONE thing that differs between the two pages that render this.
 *
 * Partner With Us imports this component rather than copying it, so that every figure,
 * the panel and the mat stay identical on both pages by construction. Its
 * heading is the exception — that page calls the section "Housing, solved at global
 * scale." — so it comes in as a prop rather than forking the component. The default is
 * this page's own wording, so List With Us renders exactly as it did before the prop
 * existed.
 */
interface WhyPartnersProps {
  /** Translation key for the section heading. */
  heading?: string;
}

const WhyPartners = ({ heading = "list.v2.whypartners.heading" }: WhyPartnersProps) => {
  const { t } = useTranslationHook();

  // Starts the count when a third of the panel is on screen, once.
  const gridRef = useRef<HTMLDivElement>(null);
  const [counting, setCounting] = useState(false);
  // ⚠️  THE CELLS ARE ONLY HIDDEN ONCE THIS IS TRUE, which is on mount and never on the server.
  // Hiding them in plain CSS would leave an empty panel for anyone whose JS is slow or blocked.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    setArmed(!window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  // Hovering anywhere on a cell replays its mark, in step with the shimmer.
  // `InlineLottie` replays on `pointerenter` of its own host (and only on hover-capable pointers),
  // so this forwards the cell's enter to it. `.mark` has `pointer-events: none`, so the icon no
  // longer answers its own hover — otherwise moving onto it would restart the animation mid-play.
  const replayMark = (cell: HTMLElement) => {
    cell.querySelector(`.${styles.mark} > span`)?.dispatchEvent(new PointerEvent("pointerenter"));
  };
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof IntersectionObserver === "undefined") {
      setCounting(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setCounting(true);
        observer.disconnect();
      },
      { threshold: 0.35 },
    );
    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  // Split the heading at its first comma so the mark can sit between the clauses —
  // see the note on the `h2` below for why this is computed rather than keyed.
  const headingText = t(heading);
  const commaAt = headingText.search(/[,\uff0c]/);
  const headBefore = commaAt > 0 ? headingText.slice(0, commaAt) : headingText;
  // ⚠️  THE COMMA IS DROPPED, NOT MOVED. The globe mark already separates the clauses, so the
  // punctuation was left dangling after the mark at the end of line one. It is cut
  // from the RENDER only — `list.v2.whypartners.heading` still reads "Global reach,
  // built for bookings" in all seven locales, so nothing had to be re-translated and
  // the split below still has its seam to find.
  //
  // `trimStart` because the source has a space after that comma, which would
  // otherwise open line two.
  const headRest = commaAt > 0 ? headingText.slice(commaAt + 1).trimStart() : "";

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Reveal as="h2" className={styles.heading}>
          {/* ⚠️  THE MARK SITS AFTER THE FIRST CLAUSE, and the split is computed from
              the translated string rather than baked into the keys.
              "Global reach" | mark | ", built for bookings".

              Every other v2 headline splits its key into a `title.line1` / `line2`
              pair for exactly this. This heading cannot: it is ONE key and it is a
              PROP — Partner With Us passes its own "Housing, solved at global
              scale." — so a second key here would only be right for one of the two
              callers, and both sentences happen to break at their first comma.

              Matched on ASCII and FULLWIDTH commas: zh-Hans/zh-Hant use U+FF0C, and
              a `,`-only search would drop the mark to the end of the Chinese line
              while leaving it mid-sentence everywhere else.

              No comma in a locale's string means no split, and the mark trails —
              which is where it sat before this and is a correct place for it, not a
              broken one.

              ⚠️  NO `{" "}` BEFORE THE MARK, which inverts the rule the hero's
              headline follows. There a real space is required, because the text
              either side are two words. Here the mark is followed by a COMMA, and a
              real space would put `h2.textContent` at "Global reach , built for
              bookings" — a space before punctuation, which is what a screen reader
              reads out and what a copy-paste carries. The gap is margin on
              `.headingMark` instead, so it exists in layout and not in the string. */}
          {headBefore}
          <span className={styles.headingMark}>
            <InlineLottie
              src={lottieEarthMark}
              size={44}
              scale={1.1}
              delay={260}
              className={styles.headingMarkArt}
            />
          </span>
          {/* ⚠️  ONE LINE. This used to break after the mark, which stacked the heading into
              two centred lines — the only section title on the page that did, and so the one
              that read as larger than the rest though all nine are the same 40px. On a narrow
              screen it still wraps naturally; nothing forces the break any more.

              The space is a real character for the same reason the old one was: it keeps
              `h2.textContent` at "Global reach built for bookings". */}{" "}
          {headRest}
        </Reveal>

        {/* ⚠️  TWO ELEMENTS, AND THE OUTER ONE IS THE MAT. The hatched band around the panel cannot be
          drawn by the panel itself: a `box-shadow` can only be a flat colour, and a `::before` at
          `z-index: -1` paints ABOVE its own parent's background rather than behind it — the spec
          paints a stacking context's background first and negative children after. Both were tried;
          the second put the hatch straight over the figures.

          So the mat is a real PARENT with 8px of padding, and the panel's own background covers its
          middle for free. `Reveal` sits on the mat rather than the panel so the two animate as one
          piece instead of the panel sliding inside a static frame. */}
        <Reveal className={styles.frame}>
          <div
            className={`${styles.grid} ${armed ? styles.armed : ""} ${
              counting ? styles.inView : ""
            }`}
            ref={gridRef}
          >
            {FIGURES.map((figure, i) => (
              <div
                className={styles.card}
                key={figure.label}
                style={{ "--i": i, "--accent": ACCENTS[i] } as React.CSSProperties}
                onMouseEnter={(e) => replayMark(e.currentTarget)}
              >
                {/* ⚠️  `autoPlay` WITH A STAGGERED `delay`, not the controlled `play` prop.
                  `InlineLottie` ignores `delay` on its controlled path, so a wave would otherwise
                  need ten timers here. Capped at the ninth so the last mark is not left waiting most
                  of a second.

                  ⚠️  These used to mount only when someone pressed the variant switch, so playing on
                  mount was always in view. With the switch gone this section can be below the fold on
                  load and the wave may run unseen — if that matters, drive `play` from an
                  IntersectionObserver on the section and stagger it with timers. */}
                <span className={styles.mark}>
                  <InlineLottie
                    src={figure.lottie}
                    size={MARK_SIZE}
                    autoPlay
                    delay={Math.min(i, 8) * 60}
                  />
                </span>

                <div className={styles.cardCopy}>
                  <p className={styles.cardTitle}>
                    <CountUp value={figure.value} index={i} run={counting} />
                  </p>
                  <p className={styles.cardLabel}>{t(figure.label)}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default wrapperHOC(WhyPartners, {
  componentName: "WhyPartners-ListWithUs",
  showForChina: true,
});
